import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { leadCategories } from '@/mocks/home';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/daimj7roh653ivfvogg0';

const inputClass =
  'w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/40 transition-colors';

export default function SubmitForm() {
  const [searchParams] = useSearchParams();
  const prefillType = searchParams.get('type') ?? '';
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [ticketId, setTicketId] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setTicketId('');
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      const text = typeof value === 'string' ? value.trim() : value;
      if (typeof text === 'string' && text === '') return;
      params.append(key, String(text));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        setTicketId(`TG-${Date.now().toString().slice(-6)}`);
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(
          isSpam
            ? '提交未通过校验，请检查内容后重新提交。'
            : serverMsg || '提交失败，请稍后重试，或通过私信渠道联系我们。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交未成功。请稍后重试，你的内容仍保留在表单中。');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 md:p-10 rounded-lg bg-background-50 border border-secondary-200">
        <span className="w-14 h-14 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
          <i className="ri-checkbox-circle-line text-3xl"></i>
        </span>
        <h2 className="mt-6 font-heading text-3xl text-foreground-950">线索已递到编辑手里</h2>
        <p className="mt-4 text-sm leading-7 text-foreground-600">
          你的投稿已进入线索库，编辑会在 36 小时内完成初步判断。如果留下了联系方式，
          被采用或需要补充材料时，编辑会主动回信。
        </p>
        {ticketId && (
          <p className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-background-100 text-sm text-foreground-700">
            <i className="ri-hashtag"></i>
            本次线索编号：<strong className="font-bold text-foreground-950">{ticketId}</strong>
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            setStatus('idle');
            setTicketId('');
          }}
          className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line text-base"></i>
          再投一条线索
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200"
      noValidate={false}
    >
      <div className="flex items-center gap-3 pb-6 mb-6 border-b border-background-200">
        <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
          <i className="ri-draft-line text-xl"></i>
        </span>
        <div>
          <h2 className="text-lg font-bold text-foreground-950">填写线索投稿表单</h2>
          <p className="text-xs text-foreground-500 mt-0.5">带 * 为必填项，预计用时 3 分钟</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-2">
          <label htmlFor="nickname" className="text-sm font-medium text-foreground-800">
            昵称或化名<span className="text-foreground-400 font-normal">（选填）</span>
          </label>
          <input id="nickname" name="nickname" type="text" placeholder="例如：夜航船" className={inputClass} />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="email" className="text-sm font-medium text-foreground-800">
            回信邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="used@example.com"
            className={inputClass}
          />
          <span className="text-xs text-foreground-500">仅编辑可见，用于告知核实结果</span>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="category" className="text-sm font-medium text-foreground-800">
            线索类型 <span className="text-primary-600">*</span>
          </label>
          <select id="category" name="category" required defaultValue={prefillType} className={inputClass}>
            <option value="" disabled>
              请选择线索所属方向
            </option>
            {leadCategories.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
            <option value="其他方向">其他方向</option>
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="anonymous" className="text-sm font-medium text-foreground-800">
            投稿方式 <span className="text-primary-600">*</span>
          </label>
          <select id="anonymous" name="anonymous" required defaultValue="匿名投稿（不公开身份）" className={inputClass}>
            <option value="匿名投稿（不公开身份）">匿名投稿（不公开身份）</option>
            <option value="署名投稿（可公开化名）">署名投稿（可公开化名）</option>
          </select>
        </div>

        <div className="flex flex-col gap-2 sm:col-span-2">
          <label htmlFor="title" className="text-sm font-medium text-foreground-800">
            线索标题 <span className="text-primary-600">*</span>
          </label>
          <input
            id="title"
            name="title"
            type="text"
            required
            placeholder="一句话概括你想说的事，例如：某剧组在杭州补拍现场临时更换主角"
            className={inputClass}
          />
        </div>

        <div className="flex flex-col gap-2 sm:col-span-2">
          <label htmlFor="content" className="text-sm font-medium text-foreground-800">
            线索详情 <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="content"
            name="content"
            required
            maxLength={500}
            rows={6}
            placeholder="请说明大致时间、地点、涉及的人或事、你了解到的途径。写清这些，编辑核实会快很多。"
            className={`${inputClass} resize-y leading-7`}
          ></textarea>
          <span className="text-xs text-foreground-500">限 500 字以内，请勿填写与线索无关的个人隐私信息</span>
        </div>

        <div className="flex flex-col gap-2 sm:col-span-2">
          <label htmlFor="source" className="text-sm font-medium text-foreground-800">
            相关链接或来源<span className="text-foreground-400 font-normal">（选填）</span>
          </label>
          <input
            id="source"
            name="source"
            type="text"
            placeholder="公开报道链接、社交平台帖子链接等，便于编辑交叉核实"
            className={inputClass}
          />
        </div>
      </div>

      <div className="honeypot-field" aria-hidden="true">
        <label htmlFor="contact_alt">备用联系方式</label>
        <input
          id="contact_alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
        />
      </div>

      {status === 'error' && formError && (
        <div className="mt-6 flex items-start gap-3 p-4 rounded-md bg-primary-100 border border-primary-200">
          <i className="ri-error-warning-line text-xl text-primary-700"></i>
          <p className="text-sm leading-6 text-foreground-800">{formError}</p>
        </div>
      )}

      <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-xs leading-6 text-foreground-500 max-w-sm">
          提交即表示你已阅读投稿指南。我们会对线索来源进行保护，但请确保你提供的信息不包含违法违规内容。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              提交中
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill text-base"></i>
              提交线索
            </>
          )}
        </button>
      </div>
    </form>
  );
}